import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  MapPin, Clock, CheckCircle, Circle, Play, Pause, LogOut as LogOutIcon,
  ChevronDown, ChevronUp, Phone, User, FileText, RefreshCw, Navigation
} from 'lucide-react';
import { API } from '../utils/apiConfig';
import axios from 'axios';
import './B2BDailyRoute.css';

export default function B2BDailyRoute() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dutyStatus, setDutyStatus] = useState('offline');
  const [updatingStop, setUpdatingStop] = useState(null);
  const [expandedRoute, setExpandedRoute] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  const loadRoutes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/b2b/user/my-routes?date=${selectedDate}`);
      setRoutes(res.data.routes || []);
      if (res.data.routes?.length > 0 && expandedRoute === null) {
        setExpandedRoute(res.data.routes[0].id);
      }
    } catch (err) {
      console.error('Failed to load routes:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => { loadRoutes(); }, [loadRoutes]);

  const sendDutyStatus = async (status) => {
    try {
      let lat = null, lng = null;
      if (navigator.geolocation) {
        const pos = await new Promise((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 })
        ).catch(() => null);
        if (pos) { lat = pos.coords.latitude; lng = pos.coords.longitude; }
      }
      await axios.post(`${API}/b2b/user/status`, { status, lat, lng });
      setDutyStatus(status);
    } catch (err) {
      console.error('Status update failed:', err);
    }
  };

  const completeStop = async (stopId, newStatus) => {
    setUpdatingStop(stopId);
    try {
      await axios.put(`${API}/b2b/user/stop/${stopId}/complete`, { status: newStatus });
      loadRoutes();
    } catch (err) {
      console.error('Stop update failed:', err);
    } finally {
      setUpdatingStop(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle size={16} className="stop-icon done" />;
      case 'arrived': return <Navigation size={16} className="stop-icon arrived" />;
      default: return <Circle size={16} className="stop-icon pending" />;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'completed': return 'status-done';
      case 'arrived': return 'status-arrived';
      default: return 'status-pending';
    }
  };

  const totalStops = routes.reduce((sum, r) => sum + (r.stops?.length || 0), 0);
  const doneStops = routes.reduce((sum, r) => sum + (r.stops?.filter(s => s.status === 'completed').length || 0), 0);

  if (loading) {
    return (
      <div className="b2b-daily" data-testid="b2b-daily-loading">
        <div className="b2b-daily-loading"><RefreshCw size={24} className="spin" /> {t('admin.loading', 'Laden...')}</div>
      </div>
    );
  }

  return (
    <div className="b2b-daily" data-testid="b2b-daily-route">
      {/* Header */}
      <div className="b2b-daily-header">
        <div className="b2b-daily-header-left">
          <Navigation size={22} />
          <div>
            <h1 data-testid="b2b-daily-title">{t('b2b.einsatzplan', 'Einsatzplan')}</h1>
            <p className="b2b-org-name">{user?.org_name || 'B2B'}</p>
          </div>
        </div>
        <div className="b2b-daily-header-right">
          <input
            type="date"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            className="b2b-date-picker"
            data-testid="b2b-date-picker"
          />
          <button onClick={loadRoutes} className="b2b-refresh-btn" data-testid="b2b-refresh-btn">
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Duty Status Bar */}
      <div className="b2b-duty-bar" data-testid="b2b-duty-bar">
        <span className="b2b-duty-label">{t('b2b.dutyStatus', 'Dienststatus')}:</span>
        <div className="b2b-duty-btns">
          {[
            { key: 'dienstbeginn', label: t('b2b.dutyStart', 'Dienstbeginn'), icon: <Play size={14} />, cls: 'duty-start' },
            { key: 'pause', label: t('b2b.dutyPause', 'Pause'), icon: <Pause size={14} />, cls: 'duty-pause' },
            { key: 'dienstende', label: t('b2b.dutyEnd', 'Dienstende'), icon: <LogOutIcon size={14} />, cls: 'duty-end' },
          ].map(btn => (
            <button
              key={btn.key}
              onClick={() => sendDutyStatus(btn.key)}
              className={`b2b-duty-btn ${btn.cls} ${dutyStatus === btn.key ? 'active' : ''}`}
              data-testid={`b2b-duty-${btn.key}`}
            >
              {btn.icon} {btn.label}
            </button>
          ))}
        </div>
      </div>

      {/* Progress */}
      {totalStops > 0 && (
        <div className="b2b-progress" data-testid="b2b-progress">
          <div className="b2b-progress-bar">
            <div className="b2b-progress-fill" style={{ width: `${(doneStops / totalStops) * 100}%` }} />
          </div>
          <span className="b2b-progress-text">
            {t('b2b.stopsProgress', '{{done}} von {{total}} erledigt', { done: doneStops, total: totalStops })}
          </span>
        </div>
      )}

      {/* Routes */}
      {routes.length === 0 ? (
        <div className="b2b-empty" data-testid="b2b-no-routes">
          <MapPin size={48} strokeWidth={1} />
          <p>{t('b2b.noRoutesToday', 'Keine Routen fuer heute.')}</p>
        </div>
      ) : (
        <div className="b2b-routes-list">
          {routes.map(route => (
            <div key={route.id} className="b2b-route-card" data-testid={`b2b-route-${route.id}`}>
              <button
                className="b2b-route-header"
                onClick={() => setExpandedRoute(expandedRoute === route.id ? null : route.id)}
                data-testid={`b2b-route-toggle-${route.id}`}
              >
                <div className="b2b-route-info">
                  <span className="b2b-route-name">{route.route_name}</span>
                  <span className={`b2b-route-status ${route.status}`}>{route.status}</span>
                  <span className="b2b-route-stops-count">{route.total_stops} {t('b2b.totalStops', 'Stopps')}</span>
                </div>
                {expandedRoute === route.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>

              {expandedRoute === route.id && (
                <div className="b2b-stops-table-wrap" data-testid={`b2b-stops-table-${route.id}`}>
                  <table className="b2b-stops-table">
                    <thead>
                      <tr>
                        <th className="col-nr">{t('b2b.stopNr', 'Nr.')}</th>
                        <th className="col-patient">{t('b2b.patient', 'Auftrag / Patient')}</th>
                        <th className="col-address">{t('b2b.addressArrival', 'Adresse')}</th>
                        <th className="col-time">{t('b2b.departure', 'Abfahrt')}</th>
                        <th className="col-duration">{t('b2b.durationOnSite', 'Dauer (Min.)')}</th>
                        <th className="col-task">{t('b2b.activity', 'Taetigkeit')}</th>
                        <th className="col-action">{t('b2b.status', 'Status')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(route.stops || []).map(stop => (
                        <tr key={stop.id} className={`b2b-stop-row ${getStatusClass(stop.status)}`} data-testid={`b2b-stop-${stop.id}`}>
                          <td className="col-nr">
                            {getStatusIcon(stop.status)}
                            <span>{stop.stop_order}</span>
                          </td>
                          <td className="col-patient">
                            <div className="patient-info">
                              <span className="patient-name">
                                <User size={13} /> {stop.contact_name || '—'}
                              </span>
                              {stop.notes && (
                                <span className="patient-phone">
                                  <Phone size={11} /> {stop.notes}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="col-address">
                            <MapPin size={13} /> {stop.address}{stop.city ? `, ${stop.city}` : ''}
                          </td>
                          <td className="col-time">
                            <Clock size={13} /> {stop.scheduled_time ? stop.scheduled_time.slice(0, 5) : '—'}
                          </td>
                          <td className="col-duration">{stop.duration_min || 30}</td>
                          <td className="col-task">
                            <FileText size={13} /> {stop.task_description || '—'}
                          </td>
                          <td className="col-action">
                            {stop.status === 'completed' ? (
                              <span className="badge-done"><CheckCircle size={14} /> {t('b2b.markDone', 'Erledigt')}</span>
                            ) : stop.status === 'arrived' ? (
                              <button
                                className="b2b-action-btn done"
                                onClick={() => completeStop(stop.id, 'completed')}
                                disabled={updatingStop === stop.id}
                                data-testid={`b2b-complete-${stop.id}`}
                              >
                                <CheckCircle size={14} /> {t('b2b.markDone', 'Erledigt')}
                              </button>
                            ) : (
                              <button
                                className="b2b-action-btn arrive"
                                onClick={() => completeStop(stop.id, 'arrived')}
                                disabled={updatingStop === stop.id}
                                data-testid={`b2b-arrive-${stop.id}`}
                              >
                                <Navigation size={14} /> {t('b2b.markArrived', 'Angekommen')}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {doneStops === totalStops && totalStops > 0 && (
        <div className="b2b-all-done" data-testid="b2b-all-done">
          <CheckCircle size={32} /> {t('b2b.allStopsDone', 'Alle Stopps erledigt!')}
        </div>
      )}
    </div>
  );
}
